/**
 * @architecture-header 1
 * @layer module
 * @domain assistants
 * @responsibility List the teacher's AI assistants and hand the selected one to the editor.
 * @state-owner dataset store holds the personas; local state holds the unsaved new one
 * @allowed-imports React; dataset and workspace stores; shared hooks; module model and components
 * @forbidden direct API client; student-only surfaces
 * @public-api settings dialog
 * @side-effects persona create, update, delete and default changes
 * @tests modules/assistants/model/assistant.test.ts and the workspace Playwright flows
 */

import { useState } from 'react';
import type { AssistantRecord } from '../../../shared/lib/model';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { assistantSummary, blankAssistant, defaultAssistant } from '../model/assistant';
import { AssistantEditor } from './AssistantEditor';

export function AssistantSettingsPage() {
  const t = useTranslator();
  const assistants = useDatasetStore((state) => state.assistants);
  const notify = useWorkspaceUiStore((state) => state.notify);
  /** A persona being created lives here until it validates; the store only holds usable ones. */
  const [pending, setPending] = useState<AssistantRecord | null>(null);
  const [selectedId, setSelectedId] = useState(() => defaultAssistant(assistants)?.id ?? '');

  const editing = pending ?? assistants.find((item) => item.id === selectedId) ?? assistants[0];

  const startNew = () => {
    const draft = blankAssistant(useDatasetStore.getState().allocateAssistantId());
    setPending(draft);
    setSelectedId(draft.id);
  };

  const save = (assistant: AssistantRecord) => {
    useDatasetStore.getState().saveAssistant(assistant);
    setPending(null);
    setSelectedId(assistant.id);
    notify(`${assistant.name} ${t('已儲存')}`);
  };

  const remove = (assistant: AssistantRecord) => {
    if (pending?.id === assistant.id) {
      setPending(null);
      setSelectedId(defaultAssistant(assistants)?.id ?? '');
      return;
    }
    useDatasetStore.getState().removeAssistant(assistant.id);
    setSelectedId(defaultAssistant(assistants)?.id ?? '');
    notify(`${assistant.name} ${t('已刪掉')}`);
  };

  const rows = pending ? [...assistants, pending] : assistants;

  return (
    <div className="asgrid">
      <aside className="aslist">
        <div className="asnew">
          <button className="bt sm pri" type="button" onClick={startNew}>
            {t('新增助教')}
          </button>
        </div>
        {rows.map((assistant) => (
          <button
            className={`asitem ${assistant.id === editing?.id ? 'on' : ''}`}
            key={assistant.id}
            type="button"
            onClick={() => {
              if (pending && pending.id !== assistant.id) setPending(null);
              setSelectedId(assistant.id);
            }}
          >
            <span className="asie" style={{ ['--accent' as string]: assistant.accent }}>
              {assistant.emoji}
            </span>
            <span className="asit">
              <b>
                {assistant.name || t('未命名助教')}
                {assistant.isDefault ? <em className="asbadge">{t('預設')}</em> : null}
              </b>
              <span>{assistant.name ? assistantSummary(assistant) : t('還沒設定完')}</span>
            </span>
          </button>
        ))}
        <p className="asnote">
          {t('派發活動時可以指定其中一位。沒有指定的活動一律由預設助教接手。')}
        </p>
      </aside>
      {editing ? (
        <AssistantEditor
          assistant={editing}
          key={editing.id}
          onDelete={() => remove(editing)}
          onSave={save}
        />
      ) : null}
    </div>
  );
}
